import { useCallback } from 'react';
import type { OnValueChange } from 'react-number-format';
import { NumericFormat } from 'react-number-format';

import type { FormControlProps } from '../FormControl';
import { FormControl } from '../FormControl';

import type { InputProps } from './Input';
import { Input } from './Input';

export interface InputNumberFormatFieldProps
	extends
		FormControlProps,
		Omit<InputProps, 'onChange' | 'value' | 'defaultValue' | 'type'> {
	value?: string | number;
	defaultValue?: string | number;
	decimalScale?: number;
	onChange: (value: string) => void;
	allowNegative?: boolean;
}

export function InputNumberFormatField({
	label,
	labelStyle,
	helperText,
	helperStyle,
	error,
	outerStyle,
	decimalScale,
	onChange,
	value,
	defaultValue,
	inputRef,
	...other
}: InputNumberFormatFieldProps) {
	const scale = decimalScale ?? 2;

	const onValueChange = useCallback<OnValueChange>(
		({ value: newValue }, { source }) => {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
			if (source === 'event') {
				/**
				 * We only care about changes that come from the user.
				 * Changes that come from the parent component should be
				 * handled by the parent component.
				 */
				onChange(newValue);
			}
		},
		[onChange]
	);

	return (
		<FormControl
			label={label}
			labelStyle={labelStyle}
			helperText={helperText}
			helperStyle={helperStyle}
			error={error}
			outerStyle={outerStyle}
		>
			<NumericFormat
				getInputRef={inputRef}
				thousandSeparator
				valueIsNumericString
				decimalScale={scale}
				fixedDecimalScale
				customInput={Input}
				value={value}
				onValueChange={onValueChange}
				error={error}
				{...other}
			/>
		</FormControl>
	);
}
